import React, { useEffect, useState } from 'react';
import api, { isSilentNetworkError } from '../../services/api';
import AdminModal from '../components/AdminModal';
import styles from './VipPlanManagement.module.css';

const emptyForm = {
  id: '',
  name: '',
  price: '',
  currency: 'CNY',
  period_days: 30,
  unit_label: '月',
  period_label: '1个月',
  badge: '',
  savings_text: '',
  features: ['无限章节阅读', '无限收藏单词 / 句子', '无限添加学习笔记', '章节 PDF 下载'],
  stripe_price_id: '',
  is_active: true,
  sort_order: 0,
};

function VipPlanManagement() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [featuresText, setFeaturesText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/plans');
      setPlans(data || []);
    } catch (error) {
      console.error('获取套餐列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData(emptyForm);
    setFeaturesText(emptyForm.features.join('\n'));
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      id: plan.id,
      name: plan.name,
      price: plan.price,
      currency: plan.currency,
      period_days: plan.period_days,
      unit_label: plan.unit_label,
      period_label: plan.period_label,
      badge: plan.badge || '',
      savings_text: plan.savings_text || '',
      features: plan.features || [],
      stripe_price_id: plan.stripe_price_id,
      is_active: plan.is_active,
      sort_order: plan.sort_order,
    });
    setFeaturesText((plan.features || []).join('\n'));
    setShowModal(true);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFeaturesTextChange = (text) => {
    setFeaturesText(text);
    const features = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    setFormData((prev) => ({ ...prev, features }));
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`确定要删除套餐「${plan.name}」(${plan.id}) 吗？此操作不可撤销。`)) {
      return;
    }
    try {
      await api.delete(`/admin/plans/${plan.id}`);
      fetchPlans();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      const detail =
        error?.response?.data?.detail || error?.message || '删除失败';
      alert(detail);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.name.trim() || !formData.price.trim() || !formData.stripe_price_id.trim()) {
      alert('请填写必填字段：套餐名称、展示价格、Stripe Price ID');
      return;
    }
    if (!editingPlan && !formData.id.trim()) {
      alert('请填写套餐 ID');
      return;
    }

    setSaving(true);
    try {
      if (editingPlan) {
        // 更新 — 不传 id 字段
        const { id: _id, ...payload } = formData;
        await api.put(`/admin/plans/${editingPlan.id}`, payload);
      } else {
        await api.post('/admin/plans', formData);
      }
      setShowModal(false);
      fetchPlans();
    } catch (error) {
      if (isSilentNetworkError(error)) return;
      const detail =
        error?.response?.data?.detail || error?.message || '操作失败';
      alert(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h2>VIP 套餐管理</h2>
          <p className={styles.subtitle}>
            管理 VIP 会员套餐的价格、功能和 Stripe 配置
          </p>
        </div>
        <button
          type="button"
          className={styles.createBtn}
          onClick={handleCreate}
        >
          + 新建套餐
        </button>
      </div>

      {loading ? (
        <p className={styles.hint}>加载中…</p>
      ) : plans.length === 0 ? (
        <div className={styles.emptyState}>
          <p>暂无套餐，请点击「新建套餐」添加</p>
        </div>
      ) : (
        <div className={styles.cardGrid}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`${styles.planCard} ${!plan.is_active ? styles.planCardInactive : ''}`}
            >
              <div className={styles.planCardHeader}>
                <h3>{plan.name}</h3>
                {plan.badge && (
                  <span className={styles.badge}>{plan.badge}</span>
                )}
                {!plan.is_active && (
                  <span className={styles.inactiveBadge}>已停用</span>
                )}
              </div>

              <div className={styles.priceRow}>
                <span className={styles.priceSymbol}>¥</span>
                <span className={styles.priceAmount}>{plan.price}</span>
                <span className={styles.priceUnit}>/{plan.unit_label}</span>
              </div>

              {plan.savings_text && (
                <p className={styles.savings}>{plan.savings_text}</p>
              )}

              <div className={styles.infoRows}>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>套餐 ID</span>
                  <span className={styles.infoValue}>{plan.id}</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>有效天数</span>
                  <span className={styles.infoValue}>{plan.period_days} 天</span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>Stripe Price ID</span>
                  <span className={`${styles.infoValue} ${styles.mono}`}>
                    {plan.stripe_price_id}
                  </span>
                </div>
                <div className={styles.infoRow}>
                  <span className={styles.infoLabel}>排序</span>
                  <span className={styles.infoValue}>{plan.sort_order}</span>
                </div>
              </div>

              <div className={styles.featureList}>
                {(plan.features || []).map((f, i) => (
                  <div key={i} className={styles.featureItem}>
                    <span className={styles.featureCheck}>✓</span>
                    {f}
                  </div>
                ))}
              </div>

              <div className={styles.cardActions}>
                <button
                  type="button"
                  className={styles.editBtn}
                  onClick={() => handleEdit(plan)}
                >
                  编辑
                </button>
                <button
                  type="button"
                  className={styles.deleteBtn}
                  onClick={() => handleDelete(plan)}
                >
                  删除
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 编辑 / 新建弹窗 */}
      {showModal && (
        <AdminModal
          title={editingPlan ? `编辑套餐：${editingPlan.name}` : '新建套餐'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          submitText={saving ? '保存中…' : '保存'}
          size="large"
        >
          <div className={styles.formGrid}>
            {/* 套餐 ID（仅新建时可编辑） */}
            <div className={styles.formGroup}>
              <label>套餐 ID *</label>
              <input
                type="text"
                value={formData.id}
                disabled={!!editingPlan}
                onChange={(e) => handleChange('id', e.target.value)}
                placeholder="如 monthly / yearly"
              />
            </div>

            <div className={styles.formGroup}>
              <label>套餐名称 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="如 月费会员"
              />
            </div>

            <div className={styles.formGroup}>
              <label>展示价格 *</label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="如 4.9"
              />
            </div>

            <div className={styles.formGroup}>
              <label>货币</label>
              <input
                type="text"
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                placeholder="CNY"
              />
            </div>

            <div className={styles.formGroup}>
              <label>有效天数 *</label>
              <input
                type="number"
                min="1"
                value={formData.period_days}
                onChange={(e) =>
                  handleChange('period_days', parseInt(e.target.value, 10) || 1)
                }
              />
            </div>

            <div className={styles.formGroup}>
              <label>单位标签</label>
              <input
                type="text"
                value={formData.unit_label}
                onChange={(e) => handleChange('unit_label', e.target.value)}
                placeholder="月 / 年"
              />
            </div>

            <div className={styles.formGroup}>
              <label>周期描述</label>
              <input
                type="text"
                value={formData.period_label}
                onChange={(e) => handleChange('period_label', e.target.value)}
                placeholder="1个月 / 12个月"
              />
            </div>

            <div className={styles.formGroup}>
              <label>推荐标签</label>
              <input
                type="text"
                value={formData.badge}
                onChange={(e) => handleChange('badge', e.target.value)}
                placeholder="推荐（留空则不显示）"
              />
            </div>

            <div className={styles.formGroup}>
              <label>省钱文案</label>
              <input
                type="text"
                value={formData.savings_text}
                onChange={(e) => handleChange('savings_text', e.target.value)}
                placeholder="节省 ¥19.8（留空则不显示）"
              />
            </div>

            <div className={styles.formGroup}>
              <label>Stripe Price ID *</label>
              <input
                type="text"
                value={formData.stripe_price_id}
                onChange={(e) =>
                  handleChange('stripe_price_id', e.target.value)
                }
                placeholder="price_xxxx"
              />
              <small className={styles.formHint}>
                在 Stripe Dashboard 创建 Price 后获取的 ID
              </small>
            </div>

            <div className={styles.formGroup}>
              <label>排序</label>
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) =>
                  handleChange('sort_order', parseInt(e.target.value, 10) || 0)
                }
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleChange('is_active', e.target.checked)
                  }
                />
                启用此套餐
              </label>
            </div>

            <div className={`${styles.formGroup} ${styles.formGroupFull}`}>
              <label>功能列表（每行一项）</label>
              <textarea
                rows={5}
                value={featuresText}
                onChange={(e) => handleFeaturesTextChange(e.target.value)}
                placeholder={'无限章节阅读\n无限收藏单词 / 句子\n章节 PDF 下载'}
              />
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default VipPlanManagement;

